/**
 * Items Page - Complete item database
 */
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { Search, Filter, Package, Loader2, ExternalLink } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { cn } from '../lib/utils';
import { trackEvent } from '../lib/analytics';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';

const POKEAPI_BASE = 'https://pokeapi.co/api/v2';
const ITEMS_PER_PAGE = 50;

// Main item categories for filtering (grouped for better UX)
const CATEGORY_GROUPS = {
  balls: {
    label: { en: 'Poké Balls', es: 'Poké Balls' },
    categories: ['standard-balls', 'special-balls', 'apricorn-balls']
  },
  healing: {
    label: { en: 'Medicine', es: 'Medicina' },
    categories: ['healing', 'medicine', 'status-cures', 'revival', 'pp-recovery', 'vitamins']
  },
  battle: {
    label: { en: 'Battle Items', es: 'Objetos de Batalla' },
    categories: ['stat-boosts', 'held-items', 'choice', 'type-enhancement', 'plates', 'z-crystals', 'bad-held-items', 'species-specific']
  },
  megastones: {
    label: { en: 'Mega Stones', es: 'Megapiedras' },
    categories: ['mega-stones']
  },
  evolution: {
    label: { en: 'Evolution', es: 'Evolución' },
    categories: ['evolution']
  },
  berries: {
    label: { en: 'Berries', es: 'Bayas' },
    categories: ['in-a-pinch', 'picky-healing', 'type-protection', 'effort-drop', 'baking-only']
  },
  training: {
    label: { en: 'Training', es: 'Entrenamiento' },
    categories: ['effort-training', 'training', 'nature-mints', 'species-candies']
  },
  tms: {
    label: { en: 'TMs & HMs', es: 'MTs y MOs' },
    categories: ['all-machines', 'tm-materials']
  },
  keyitems: {
    label: { en: 'Key Items', es: 'Objetos Clave' },
    categories: ['plot-advancement', 'gameplay', 'event-items', 'dex-completion', 'data-cards', 'memories']
  },
  treasures: {
    label: { en: 'Treasures', es: 'Tesoros' },
    categories: ['loot', 'collectibles', 'jewels']
  },
  other: {
    label: { en: 'Other', es: 'Otros' },
    categories: ['other', 'flutes', 'scarves', 'all-mail', 'mulch', 'spelunking', 'catching-bonus', 'dynamax-crystals', 'tera-shard', 'sandwich-ingredients', 'curry-ingredients', 'picnic']
  }
};

// ItemCard component
function ItemCard({ item, onClick }) {
  const { language } = useApp();
  
  const displayName = item.names?.find(n => n.language.name === (language === 'es' ? 'es' : 'en'))?.name 
    || item.name.replace(/-/g, ' ');
  
  const effectText = item.effect_entries?.find(e => e.language.name === (language === 'es' ? 'es' : 'en'))?.short_effect
    || item.effect_entries?.find(e => e.language.name === 'en')?.short_effect
    || '';
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ scale: 1.02 }}
      onClick={onClick}
      className="bg-card rounded-lg border border-border p-3 cursor-pointer hover:border-violet-500/50 transition-all"
      data-testid={`item-card-${item.id}`}
    >
      <div className="flex items-start gap-3">
        {item.sprites?.default ? (
          <img 
            src={item.sprites.default} 
            alt={displayName}
            className="w-10 h-10 object-contain bg-muted rounded"
            loading="lazy"
          />
        ) : (
          <div className="w-10 h-10 bg-muted rounded flex items-center justify-center">
            <Package className="w-5 h-5 text-muted-foreground" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-sm capitalize truncate">{displayName}</h3>
          <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
            {effectText || (language === 'es' ? 'Sin descripción' : 'No description')}
          </p>
        </div>
      </div>
    </motion.div>
  );
}

// ItemDetailModal component
function ItemDetailModal({ item, onClose }) {
  const { language } = useApp();
  
  if (!item) return null;
  
  const displayName = item.names?.find(n => n.language.name === (language === 'es' ? 'es' : 'en'))?.name 
    || item.name.replace(/-/g, ' ');
  
  const effectText = item.effect_entries?.find(e => e.language.name === (language === 'es' ? 'es' : 'en'))?.effect
    || item.effect_entries?.find(e => e.language.name === 'en')?.effect
    || '';
  
  const flavorText = item.flavor_text_entries?.find(f => f.language.name === (language === 'es' ? 'es' : 'en'))?.text
    || item.flavor_text_entries?.find(f => f.language.name === 'en')?.text
    || '';
  
  const texts = {
    cost: language === 'es' ? 'Precio' : 'Cost',
    category: language === 'es' ? 'Categoría' : 'Category',
    effect: language === 'es' ? 'Efecto' : 'Effect',
    description: language === 'es' ? 'Descripción' : 'Description',
    flingPower: language === 'es' ? 'Poder de Lanzar' : 'Fling Power',
    close: language === 'es' ? 'Cerrar' : 'Close'
  };
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-card rounded-xl border border-border p-6 max-w-md w-full shadow-2xl"
        onClick={e => e.stopPropagation()}
        data-testid="item-detail-modal"
      >
        <div className="flex items-center gap-4 mb-4">
          {item.sprites?.default ? (
            <img 
              src={item.sprites.default} 
              alt={displayName}
              className="w-16 h-16 object-contain bg-muted rounded-lg"
            />
          ) : (
            <div className="w-16 h-16 bg-muted rounded-lg flex items-center justify-center">
              <Package className="w-8 h-8 text-muted-foreground" />
            </div>
          )}
          <div>
            <h2 className="text-xl font-bold capitalize">{displayName}</h2>
            <p className="text-sm text-muted-foreground capitalize">
              {item.category?.name?.replace(/-/g, ' ')}
            </p>
          </div>
        </div>
        
        <div className="space-y-3">
          {item.fling_power && (
            <div className="flex justify-between items-center py-2 border-b border-border">
              <span className="text-sm text-muted-foreground">{texts.flingPower}</span>
              <span className="font-semibold">{item.fling_power}</span>
            </div>
          )}
          
          {effectText && (
            <div className="py-2">
              <h3 className="text-sm font-semibold mb-1">{texts.effect}</h3>
              <p className="text-sm text-muted-foreground">{effectText}</p>
            </div>
          )}
          
          {flavorText && (
            <div className="py-2 bg-muted/30 rounded-lg p-3">
              <h3 className="text-sm font-semibold mb-1">{texts.description}</h3>
              <p className="text-sm text-muted-foreground italic">"{flavorText}"</p>
            </div>
          )}
        </div>
        
        <Button
          onClick={onClose}
          className="w-full mt-4"
          variant="outline"
        >
          {texts.close}
        </Button>
      </motion.div>
    </motion.div>
  );
}

// Loading skeleton
function ItemSkeleton() {
  return (
    <div className="bg-card rounded-lg border border-border p-3 animate-pulse">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 bg-muted rounded" />
        <div className="flex-1">
          <div className="h-4 bg-muted rounded w-3/4 mb-2" />
          <div className="h-3 bg-muted rounded w-full" />
        </div>
      </div>
    </div>
  );
}

export default function Items() {
  const { language, addToCache, getFromCache } = useApp();
  
  // State
  const [allItems, setAllItems] = useState([]);
  const [itemDetails, setItemDetails] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [displayCount, setDisplayCount] = useState(ITEMS_PER_PAGE);
  const [selectedItem, setSelectedItem] = useState(null);
  
  // Refs
  const loadMoreRef = useRef(null);
  const observerRef = useRef(null);
  
  // Fetch all items list
  useEffect(() => {
    const fetchItems = async () => {
      setLoading(true);
      try {
        const cached = getFromCache('items-list');
        if (cached) {
          setAllItems(cached);
          setLoading(false);
          return;
        }
        
        // Fetch items in batches
        let allItemsList = [];
        let offset = 0;
        const limit = 200; // Larger chunks
        
        while (true) {
          const response = await fetch(`${POKEAPI_BASE}/item?limit=${limit}&offset=${offset}`);
          const data = await response.json();
          
          allItemsList = [...allItemsList, ...data.results.map((item, index) => ({
            id: offset + index + 1,
            name: item.name,
            url: item.url
          }))];
          
          if (!data.next) break;
          offset += limit;
          
          // Cover all items including Gen 9 DLC
          if (offset >= 3000) break;
        }
        
        addToCache('items-list', allItemsList);
        setAllItems(allItemsList);
      } catch (error) {
        console.error('Error fetching items:', error);
      }
      setLoading(false);
    };
    
    fetchItems();
  }, [addToCache, getFromCache]);
  
  // Fetch item details
  const fetchItemDetail = useCallback(async (item) => {
    if (itemDetails[item.id]) return itemDetails[item.id];
    
    const cached = getFromCache(`item-${item.id}`);
    if (cached) {
      setItemDetails(prev => ({ ...prev, [item.id]: cached }));
      return cached;
    }
    
    try {
      const response = await fetch(item.url);
      const data = await response.json();
      addToCache(`item-${item.id}`, data);
      setItemDetails(prev => ({ ...prev, [item.id]: data }));
      return data;
    } catch (error) {
      console.error(`Error fetching item ${item.id}:`, error);
      return null;
    }
  }, [itemDetails, addToCache, getFromCache]);

  // Prefetch items when category changes
  useEffect(() => {
    if (selectedCategory === 'all' || allItems.length === 0) return;
    
    const prefetchForCategory = async () => {
      setLoading(true);
      // Fetch details for items that might match the category
      const unfetched = allItems.filter(item => !itemDetails[item.id]).slice(0, 200);
      
      // Fetch in batches of 20
      for (let i = 0; i < unfetched.length; i += 20) {
        const batch = unfetched.slice(i, i + 20);
        await Promise.all(batch.map(item => fetchItemDetail(item)));
      }
      setLoading(false);
    };
    
    prefetchForCategory();
  }, [selectedCategory, allItems]);
  
  // Filter items
  const filteredItems = useMemo(() => {
    let result = [...allItems];
    
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(item => 
        item.name.toLowerCase().includes(query) ||
        itemDetails[item.id]?.names?.some(n => 
          n.name.toLowerCase().includes(query)
        )
      );
    }
    
    // Category filter
    if (selectedCategory !== 'all') {
      const categoryGroup = CATEGORY_GROUPS[selectedCategory];
      if (categoryGroup) {
        result = result.filter(item => {
          const detail = itemDetails[item.id];
          
          // If we have details, check category
          if (detail) return categoryGroup.categories.includes(detail.category?.name);
          
          // Optimization for Mega Stones: if detail is missing but name ends in 'ite', 
          // keep it for prefetching if megastones is selected
          if (selectedCategory === 'megastones') {
            const name = item.name.toLowerCase();
            return name.endsWith('ite') || name.includes('ite-x') || name.includes('ite-y') || name.includes('ite-z');
          }
          
          return false;
        });
      }
    }
    
    return result;
  }, [allItems, searchQuery, selectedCategory, itemDetails]);
  
  // Fetch details for visible items
  useEffect(() => {
    const visibleItems = filteredItems.slice(0, displayCount);
    visibleItems.forEach(item => {
      if (!itemDetails[item.id]) {
        fetchItemDetail(item);
      }
    });
  }, [filteredItems, displayCount, itemDetails, fetchItemDetail]);
  
  // Displayed items
  const displayedItems = useMemo(() => {
    return filteredItems.slice(0, displayCount);
  }, [filteredItems, displayCount]);
  
  // Infinite scroll
  useEffect(() => {
    if (loadMoreRef.current && !loading) {
      observerRef.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && displayCount < filteredItems.length && !loadingMore) {
            setLoadingMore(true);
            setTimeout(() => {
              setDisplayCount(prev => Math.min(prev + ITEMS_PER_PAGE, filteredItems.length));
              setLoadingMore(false);
            }, 300);
          }
        },
        { threshold: 0.1 }
      );
      
      observerRef.current.observe(loadMoreRef.current);
    }
    
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [loading, displayCount, filteredItems.length, loadingMore]);
  
  // Reset display count when filters change
  useEffect(() => {
    setDisplayCount(ITEMS_PER_PAGE);
  }, [searchQuery, selectedCategory]);
  
  // Handle item click
  const handleItemClick = async (item) => {
    const detail = await fetchItemDetail(item);
    if (detail) {
      setSelectedItem(detail);
      trackEvent('item_view', {
        itemName: item.name,
        itemCategory: item.category?.name || '',
      });
    }
  };
  
  const texts = {
    title: language === 'es' ? 'Objetos' : 'Items',
    subtitle: language === 'es' ? 'Base de datos completa de objetos' : 'Complete item database',
    searchPlaceholder: language === 'es' ? 'Buscar objetos...' : 'Search items...',
    all: language === 'es' ? 'Todos' : 'All',
    showing: language === 'es' ? 'Mostrando' : 'Showing',
    of: language === 'es' ? 'de' : 'of',
    items: language === 'es' ? 'objetos' : 'items',
    loading: language === 'es' ? 'Cargando...' : 'Loading...',
    noResults: language === 'es' ? 'No se encontraron objetos' : 'No items found'
  };
  
  return (
    <div className="min-h-screen" data-testid="items-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-6"
        >
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-2">
            <span className="text-violet-600 dark:text-violet-400">{texts.title}</span>
          </h1>
          <p className="text-muted-foreground">{texts.subtitle}</p>
        </motion.div>
        
        {/* Search and Filters */}
        <div className="bg-card rounded-xl border border-border p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder={texts.searchPlaceholder}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="items-search"
              />
            </div>
            
            {/* Category Filter */}
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-muted-foreground" />
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="bg-background border border-input rounded-lg px-3 py-2 text-sm"
                data-testid="items-category-filter"
              >
                <option value="all">{texts.all}</option>
                {Object.entries(CATEGORY_GROUPS).map(([key, group]) => (
                  <option key={key} value={key}>
                    {group.label[language]}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
        
        {/* Results count */}
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-muted-foreground">
            {texts.showing} {displayedItems.length} {texts.of} {filteredItems.length} {texts.items}
          </p>
        </div>
        
        {/* Items Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {Array.from({ length: 20 }).map((_, i) => (
              <ItemSkeleton key={i} />
            ))}
          </div>
        ) : displayedItems.length === 0 ? (
          <div className="text-center py-16">
            <Package className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-xl text-muted-foreground">{texts.noResults}</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {displayedItems.map((item) => (
                <ItemCard
                  key={item.id}
                  item={itemDetails[item.id] || { ...item, sprites: null }}
                  onClick={() => handleItemClick(item)}
                />
              ))}
            </div>
            
            {/* Load more trigger */}
            {displayCount < filteredItems.length && (
              <div ref={loadMoreRef} className="flex justify-center py-8">
                {loadingMore && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>{texts.loading}</span>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
      
      {/* Item Detail Modal */}
      {selectedItem && (
        <ItemDetailModal 
          item={selectedItem} 
          onClose={() => setSelectedItem(null)} 
        />
      )}
    </div>
  );
}
